import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import AppContainer from "../../Components/Accounting/AppContainer";
import Select from "react-select";
import ajaxAccounting from "../../util/accounting/remote/ajaxAccounting";
import functions from "../../util/accounting/functions";
import { canAddRow, addRowBlockedReason } from "../../util/accounting/formRows";
import toast, { Toaster } from "react-hot-toast";

/** A budget line says nothing until it names an account and a figure. */
const BUDGET_LINE_REQUIRED = [
  { key: "account_id", label: "an account" },
  { key: "budget_amount", label: "an amount", numeric: true },
];

const BudgetNew = () => {
  const navigate   = useNavigate();
  const [fyList,   setFyList]   = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [selectedFY, setSelectedFY] = useState(null);
  const [saving,   setSaving]   = useState(false);
  const [form, setForm] = useState({
    name:"", type:{ value:"annual",label:"Annual" }, description:"",
  });
  const [lines, setLines] = useState([
    { account_id:null, budget_amount:"", notes:"" },
  ]);

  useEffect(() => { fetchMeta(); }, []);

  const fetchMeta = async () => {
    const [fyRes, accRes] = await Promise.all([
      ajaxAccounting.listFinancialYears({}),
      ajaxAccounting.listAccounts({ postable_only:1 }),
    ]);
    if (fyRes.status==="OK") {
      const list = fyRes.details||[];
      setFyList(list);
      const cur = list.find((y)=>y.is_current===1)||list[0];
      if (cur) setSelectedFY({ value:cur.id, label:cur.name });
    }
    if (accRes.status==="OK") setAccounts(accRes.details||[]);
  };

  const setLine = (i,f,v) => { const u=[...lines]; u[i]={...u[i],[f]:v}; setLines(u); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name||!selectedFY) { toast.error("Budget name and financial year required"); return; }
    const validLines = lines.filter((l)=>l.account_id&&l.budget_amount);
    setSaving(true);
    // Create the budget header
    const res = await ajaxAccounting.createBudget({
      name:             form.name,
      type:             form.type?.value||"annual",
      description:      form.description,
      financial_year_id:selectedFY.value,
    });
    if (res.status==="OK") {
      const budgetId = res.details?.id;
      // Create budget lines
      if (budgetId && validLines.length) {
        for (const l of validLines) {
          await ajaxAccounting.setBudgetLine({
            budget_id:    budgetId,
            account_id:   l.account_id?.value,
            budget_amount:parseFloat(l.budget_amount),
            notes:        l.notes,
          });
        }
      }
      toast.success("Budget created!");
      navigate(`/accounting/budget`);
    } else {
      toast.error(res.message||"Failed to create budget");
    }
    setSaving(false);
  };

  const fyOptions   = fyList.map((y)=>({ value:y.id, label:y.name }));
  const typeOptions = [{ value:"annual",label:"Annual" },{ value:"quarterly",label:"Quarterly" },{ value:"monthly",label:"Monthly" }];

  // Group accounts for better UX
  const groupedAccounts = () => {
    const groups = {};
    accounts.forEach((a) => {
      const g = a.type?.charAt(0).toUpperCase()+a.type?.slice(1)||"Other";
      if (!groups[g]) groups[g]=[];
      groups[g].push({ value:a.id, label:`${a.account_code} — ${a.name}` });
    });
    return Object.entries(groups).map(([label,options])=>({ label,options }));
  };

  const totalBudget = lines.reduce((s,l)=>s+(parseFloat(l.budget_amount)||0),0);

  return (
    <AppContainer
      title="Create Budget"
      previousPageTitle="Budget"
      description="Create a new budget by setting budget amounts per account. Once created you can track actual spending against it in Budget vs Actual."
      actions={[
        { label:"Save Budget",  icon:"fa-solid fa-floppy-disk", className:"btn btn-primary",         onClick:handleSubmit, disabled:saving },
        { label:"Cancel",       icon:"fa-solid fa-xmark",       className:"btn btn-light",            onClick:()=>navigate("/accounting/budget") },
      ]}
    >
      <Toaster />
      <div className="row">
        <div className="col-lg-8">
          {/* Header fields */}
          <div className="card custom-card p-4 mb-3">
            <h6 className="fw-semibold mb-3">Budget Details</h6>
            <div className="row g-3">
              <div className="col-12">
                <label className="form-label tx-12 fw-semibold">Budget Name <span className="tx-danger">*</span></label>
                <input className="form-control" value={form.name}
                  onChange={(e)=>setForm({...form,name:e.target.value})}
                  placeholder="e.g. Annual Operations Budget 2025/26" />
              </div>
              <div className="col-lg-6">
                <label className="form-label tx-12 fw-semibold">Financial Year <span className="tx-danger">*</span></label>
                <Select options={fyOptions} value={selectedFY} onChange={setSelectedFY} placeholder="Select year" isSearchable />
              </div>
              <div className="col-lg-6">
                <label className="form-label tx-12 fw-semibold">Type</label>
                <Select options={typeOptions} value={form.type} onChange={(v)=>setForm({...form,type:v})} />
              </div>
              <div className="col-12">
                <label className="form-label tx-12 fw-semibold">Description</label>
                <textarea className="form-control" rows={2} value={form.description}
                  onChange={(e)=>setForm({...form,description:e.target.value})} />
              </div>
            </div>
          </div>

          {/* Budget lines */}
          <div className="card custom-card p-4">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h6 className="fw-semibold mb-0">Budget Lines</h6>
              <button type="button" className="btn btn-sm btn-outline-primary"
                onClick={()=>setLines([...lines,{account_id:null,budget_amount:"",notes:""}])}
                disabled={!canAddRow(lines, BUDGET_LINE_REQUIRED)}
                title={addRowBlockedReason(lines, BUDGET_LINE_REQUIRED) || "Add another line"}>
                <i className="fa-solid fa-plus me-1"></i>Add Line
              </button>
            </div>
            <div className="table-responsive">
              <table className="table table-bordered align-middle" style={{fontSize:"0.84rem"}}>
                <thead className="bg-light">
                  <tr>
                    <th style={{minWidth:"240px"}}>Account</th>
                    <th style={{width:"160px"}}>Budget Amount (UGX)</th>
                    <th>Notes</th>
                    <th style={{width:"36px"}}></th>
                  </tr>
                </thead>
                <tbody>
                  {lines.map((line,i)=>(
                    <tr key={i}>
                      <td>
                        <Select options={groupedAccounts()} value={line.account_id}
                          onChange={(v)=>setLine(i,"account_id",v)}
                          placeholder="Select account" isSearchable />
                      </td>
                      <td>
                        <input type="number" className="form-control form-control-sm" min="0" step="0.01"
                          placeholder="0.00" value={line.budget_amount}
                          onChange={(e)=>setLine(i,"budget_amount",e.target.value)} />
                      </td>
                      <td>
                        <input className="form-control form-control-sm" value={line.notes}
                          onChange={(e)=>setLine(i,"notes",e.target.value)} placeholder="Optional note" />
                      </td>
                      <td>
                        {lines.length>1&&(
                          <button type="button" className="btn btn-sm btn-light tx-danger"
                            onClick={()=>setLines(lines.filter((_,j)=>j!==i))}>
                            <i className="fa-solid fa-trash"></i>
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-light fw-semibold">
                  <tr>
                    <td>Total Budget</td>
                    <td className="tx-primary">{functions.formatCurrency(totalBudget)}</td>
                    <td colSpan={2}></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        </div>

        <div className="col-lg-4">
          <div className="card custom-card p-3 mb-3">
            <h6 className="fw-semibold mb-3">Summary</h6>
            <div className="d-flex justify-content-between mb-2 tx-13">
              <span className="tx-muted">Budget Lines</span>
              <span className="fw-bold">{lines.filter((l)=>l.account_id&&l.budget_amount).length}</span>
            </div>
            <div className="d-flex justify-content-between mb-2 tx-13">
              <span className="tx-muted">Total Budget</span>
              <span className="fw-bold tx-primary">{functions.formatCurrency(totalBudget)}</span>
            </div>
            <div className="d-flex justify-content-between mb-2 tx-13">
              <span className="tx-muted">Financial Year</span>
              <span className="fw-bold">{selectedFY?.label||"—"}</span>
            </div>
            <hr />
            <div className="alert alert-info py-2 tx-12">
              After creating, use <strong>Budget vs Actual</strong> to track spending against this budget.
            </div>
          </div>
        </div>
      </div>
    </AppContainer>
  );
};

export default BudgetNew;
